import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseOfferingApi, ExerciseActivitySummary } from "../api/CourseOfferingApi";
import { DialogApi, IDialog } from '../api/DialogApi';
import { DialogPrompt, DialogPromptApi } from '../api/DialogPromptApi';
import { ILesson, LessonApi } from '../api/LessonApi';
import { IUser } from "../api/UserApi";
// import { UserDialogPromptCourseExerciseSummary } from '../api/UserDialogPromptApi';
import AppBarContext from '../context/AppBarContext';
import { handleErrorMessage } from './ErrorDialog';
import { getClassNameDescription } from "./dialogPrompt/DialogPromptForm";

interface CourseExerciseActivityProps {
    courseOfferingId: number;
    courseId: number;
}

const createSummary = (): ExerciseActivitySummary => ({
    course_id: 0,
    lesson_id: 0,
    dialog_id: 0,
    dialog_prompt_id: 0,
    user_count: Number.MAX_SAFE_INTEGER,
    incorrect_answer_count: 0,
    correct_answer_count: 0,
    speaking_attempt_count: 0,
    mean_best_speaking_score: 0,
});

const addToSummary = (
    totalSummary: ExerciseActivitySummary,
    addedSummary: ExerciseActivitySummary
) => {
    totalSummary.user_count = addedSummary.user_count < totalSummary.user_count ? addedSummary.user_count : totalSummary.user_count;
    totalSummary.incorrect_answer_count += addedSummary.incorrect_answer_count;
    totalSummary.correct_answer_count += addedSummary.correct_answer_count;

    if (addedSummary.speaking_attempt_count > 0) {
        if (totalSummary.speaking_attempt_count == 0) {
            totalSummary.speaking_attempt_count = addedSummary.speaking_attempt_count;
            totalSummary.mean_best_speaking_score = addedSummary.mean_best_speaking_score;
        } else {
            const totalSummarySpeakingScoreSum = totalSummary.speaking_attempt_count * totalSummary.mean_best_speaking_score
            const addedSummarySpeakingScoreSum = addedSummary.speaking_attempt_count * addedSummary.mean_best_speaking_score
            totalSummary.speaking_attempt_count += addedSummary.speaking_attempt_count;
            totalSummary.mean_best_speaking_score = (totalSummarySpeakingScoreSum + addedSummarySpeakingScoreSum) / totalSummary.speaking_attempt_count
        }
    }
};

const CourseExerciseActivity: React.FC<CourseExerciseActivityProps> = ({ courseOfferingId, courseId }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [userRoster, setUserRoster] = React.useState<IUser[]>([]);

    const [lessonList, setLessonList] = React.useState<ILesson[]>([]);

    const [courseSummary, setCourseSummary] = React.useState<ExerciseActivitySummary | null>(null)
    const [lessonSummaryMap, setLessonSummaryMap] = React.useState<Record<number, ExerciseActivitySummary>>({})
    const [dialogSummaryMap, setDialogSummaryMap] = React.useState<Record<number, ExerciseActivitySummary>>({})
    const [dialogPromptSummaryMap, setDialogPromptSummaryMap] = React.useState<Record<number, ExerciseActivitySummary>>({})

    // which lessons/dialogs are expanded
    const [isCourseExpanded, setExpandCourse] = React.useState<boolean>(true);
    const [expandedLessons, setExpandedLessons] = React.useState<Set<number>>(new Set());
    const [expandedDialogs, setExpandedDialogs] = React.useState<Set<number>>(new Set());

    // indent sizes (in theme spacing units)
    const INDENT_LESSON = 4; // px padding-left multiplier for lesson rows
    const INDENT_DIALOG = 8;
    const INDENT_PROMPT = 12;

    // lazy-load caches (children by parent)
    const [dialogListByLesson, setDialogListByLesson] = React.useState<Record<number, IDialog[]>>({});
    const [promptListByDialog, setPromptListByDialog] = React.useState<Record<number, DialogPrompt[]>>({});

    const updateUserDialogPromptSummary = React.useCallback((exerciseActivitySummaryList: ExerciseActivitySummary[]) => {
        const newCourseSummary = createSummary();

        const lessonMap: Record<number, ExerciseActivitySummary> = {};
        const dialogMap: Record<number, ExerciseActivitySummary> = {};
        const dialogPromptMap: Record<number, ExerciseActivitySummary> = {};

        for (const exerciseActivitySummary of exerciseActivitySummaryList) {
            addToSummary(newCourseSummary, exerciseActivitySummary)

            // by lesson
            if (!lessonMap[exerciseActivitySummary.lesson_id]) {
                lessonMap[exerciseActivitySummary.lesson_id] = {
                    ...createSummary(),
                    course_id: exerciseActivitySummary.course_id,
                    lesson_id: exerciseActivitySummary.lesson_id,
                };
            }
            addToSummary(lessonMap[exerciseActivitySummary.lesson_id], exerciseActivitySummary);

            // by dialog
            if (!dialogMap[exerciseActivitySummary.dialog_id]) {
                dialogMap[exerciseActivitySummary.dialog_id] = {
                    ...createSummary(),
                    course_id: exerciseActivitySummary.course_id,
                    lesson_id: exerciseActivitySummary.lesson_id,
                    dialog_id: exerciseActivitySummary.dialog_id,
                };
            }
            addToSummary(dialogMap[exerciseActivitySummary.dialog_id], exerciseActivitySummary);

            // by dialog prompt
            if (!dialogPromptMap[exerciseActivitySummary.dialog_prompt_id]) {
                dialogPromptMap[exerciseActivitySummary.dialog_prompt_id] = {
                    ...createSummary(),
                    course_id: exerciseActivitySummary.course_id,
                    lesson_id: exerciseActivitySummary.lesson_id,
                    dialog_id: exerciseActivitySummary.dialog_id,
                    dialog_prompt_id: exerciseActivitySummary.dialog_prompt_id,
                };
            }
            addToSummary(dialogPromptMap[exerciseActivitySummary.dialog_prompt_id], exerciseActivitySummary);
        }
        setCourseSummary(newCourseSummary)
        setLessonSummaryMap(lessonMap)
        setDialogSummaryMap(dialogMap)
        setDialogPromptSummaryMap(dialogPromptMap)
    }, [setCourseSummary, setLessonSummaryMap, setDialogSummaryMap, setDialogPromptSummaryMap]);

    React.useEffect(() => {

        CourseOfferingApi.exerciseActivity(courseOfferingId)
            .then((response) => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to UserDialogPromptApi.getCourseSummary " + response.status);
            })
            .then((summary) => {
                updateUserDialogPromptSummary(summary);
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });

        // Fetch lessons when course changes
        LessonApi.getAll(courseId)
            .then(response => {
                const visibleList = response.data.filter((lesson) => lesson.is_hidden === false);
                setLessonList(visibleList.sort((a, b) => a.order_index - b.order_index));
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })

        try {
            CourseOfferingApi.studentRoster(courseOfferingId).then((response) => {
                setUserRoster(response.data);
            });
        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }

    }, [courseOfferingId, courseId, history, location, setErrorMessage, updateUserDialogPromptSummary]);


    const toggleCourse = () => setExpandCourse(v => !v);

    const toggleLesson = async (lessonId: number) => {
        setExpandedLessons(prev => {
            const next = new Set(prev);
            if (next.has(lessonId)) {
                next.delete(lessonId);
            } else {
                next.add(lessonId);
                // lazy-load dialogs once
                if (!dialogListByLesson[lessonId]) {
                    DialogApi.getAll(lessonId)
                        .then(resp => {
                            const visible = resp.data.filter(d => !d.is_hidden).sort((a, b) => a.order_index - b.order_index);
                            setDialogListByLesson(curr => ({ ...curr, [lessonId]: visible }));
                        })
                        .catch(err => handleErrorMessage(err, setErrorMessage, history, location));
                }
            }
            return next;
        });
    };

    const toggleDialog = async (dialogId: number) => {
        setExpandedDialogs(prev => {
            const next = new Set(prev);
            if (next.has(dialogId)) {
                next.delete(dialogId);
            } else {
                next.add(dialogId);
                // lazy-load prompts once
                if (!promptListByDialog[dialogId]) {
                    DialogPromptApi.getAll(dialogId)
                        .then(resp => {
                            const visible = resp.data.filter(p => !p.is_hidden).sort((a, b) => a.order_index - b.order_index);
                            setPromptListByDialog(curr => ({ ...curr, [dialogId]: visible }));
                        })
                        .catch(err => handleErrorMessage(err, setErrorMessage, history, location));
                }
            }
            return next;
        });
    };

    // A tiny cell renderer for summary columns
    const SummaryCells: React.FC<{ summary: ExerciseActivitySummary }> = ({ summary }) => {
        // const userPercentage = summary.user_count == Number.MAX_SAFE_INTEGER ? '0%' : `${Math.floor(summary.user_count / userRoster.length * 100)}%`
        const user_count = summary.user_count == Number.MAX_SAFE_INTEGER ? 0 : summary.user_count;
        const incorrectCorrectRatio = summary.correct_answer_count == 0 ? '' : (summary.incorrect_answer_count / summary.correct_answer_count).toFixed(2)
        return (<>
            <TableCell align="right">{user_count} / {userRoster.length}</TableCell>
            <TableCell align="right">{summary.incorrect_answer_count}</TableCell>
            <TableCell align="right">{summary.correct_answer_count}</TableCell>
            <TableCell align="right">{incorrectCorrectRatio}</TableCell>
            <TableCell align="right">{summary.speaking_attempt_count}</TableCell>
            <TableCell align="right">{summary.mean_best_speaking_score.toFixed(2)}</TableCell>
        </>)
    };

    const DialogPromptDescription: React.FC<{ dialogPrompt: DialogPrompt }> = ({ dialogPrompt }) => {
        let description = dialogPrompt.prompt.substring(0, 30)

        if ('new_word' in dialogPrompt && String(dialogPrompt.new_word).trim().length > 0) {
            description = String(dialogPrompt.new_word)
        } else if ('correct_vocabulary_word' in dialogPrompt) {
            const vocabularyWord = (dialogPrompt as { correct_vocabulary_word: { word: string, audio_sentence: string } }).correct_vocabulary_word;
            if (vocabularyWord.audio_sentence.trim().length > 0) {
                description = vocabularyWord.audio_sentence
            } else {
                description = vocabularyWord.word
            }
        } else if ('vocabulary_word_list' in dialogPrompt) {
            const vocabList = (dialogPrompt as { vocabulary_word_list: { word: string }[] }).vocabulary_word_list;
            description = vocabList[0].word + " / " + vocabList[1].word;
        } else if ('teacher_chinese_prompt' in dialogPrompt && String(dialogPrompt.teacher_chinese_prompt).trim().length > 0) {
            description = String(dialogPrompt.teacher_chinese_prompt)
        } else if ('student_response_prompt' in dialogPrompt && dialogPrompt.student_response_prompt != '<Answer/>') {
            description = String(dialogPrompt.student_response_prompt)
        }
        return (<>
            {description}<br />
            <Box sx={{ color: 'primary.main', opacity: 0.5 }}>
                {getClassNameDescription(dialogPrompt.class_name)}
            </Box>
        </>)
    };

    return (<>
        <Typography variant="h2" sx={{ color: "#002A6E", textAlign: "center", mt: 2 }}>
            Exercise Activity
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width={12} />  {/* expander column */}
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Users</TableCell>
                        <TableCell align="right">Incorrect</TableCell>
                        <TableCell align="right">Correct</TableCell>
                        <TableCell align="right">Incorrect<br />Correct<br />Ratio</TableCell>
                        <TableCell align="right">Speaking<br />Attempts</TableCell>
                        <TableCell align="right">Mean<br />Best Score</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {/* Course row */}
                    <TableRow hover>
                        <TableCell width={12}>
                            <IconButton size="small" onClick={toggleCourse}>
                                {isCourseExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                        </TableCell>
                        <TableCell>Course</TableCell>
                        {courseSummary ? <SummaryCells summary={courseSummary} /> : (
                            <>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                            </>
                        )}
                    </TableRow>

                    {/* Lessons (all in SAME TableBody) */}
                    {isCourseExpanded && lessonList.map(lesson => {
                        const lessonSummary = lessonSummaryMap[lesson.id] ? lessonSummaryMap[lesson.id] : createSummary();
                        const isLessonExpanded = expandedLessons.has(lesson.id);
                        const dialogList = dialogListByLesson[lesson.id] ? dialogListByLesson[lesson.id] : [];
                        return (
                            <React.Fragment key={`lesson-${lesson.id}`}>
                                <TableRow hover>
                                    <TableCell width={12}>
                                        <IconButton size="small" onClick={() => toggleLesson(lesson.id)}>
                                            {isLessonExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={{ pl: INDENT_LESSON }}>{lesson.name}</TableCell>
                                    <SummaryCells summary={lessonSummary} />
                                </TableRow>

                                {/* Dialogs under this lesson */}
                                {isLessonExpanded && dialogList.map(dialog => {
                                    const dialogSummary = dialogSummaryMap[dialog.id] ? dialogSummaryMap[dialog.id] : createSummary();
                                    const isDialogExpanded = expandedDialogs.has(dialog.id);
                                    const promptList = promptListByDialog[dialog.id] ? promptListByDialog[dialog.id] : [];
                                    return (
                                        <React.Fragment key={`dialog-${dialog.id}`}>
                                            <TableRow hover>
                                                <TableCell width={12}>
                                                    <IconButton size="small" onClick={() => toggleDialog(dialog.id)}>
                                                        {isDialogExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{ pl: INDENT_DIALOG }}>
                                                    {dialog.title}<br />
                                                    <Box sx={{ color: 'primary.main', opacity: 0.5 }}>
                                                        {dialog.subtitle}
                                                    </Box>
                                                </TableCell>
                                                <SummaryCells summary={dialogSummary} />
                                            </TableRow>

                                            {/* Dialog prompts */}
                                            {isDialogExpanded && promptList.map(prompt => {
                                                const dialogPromptSummary = dialogPromptSummaryMap[prompt.id] ? dialogPromptSummaryMap[prompt.id] : createSummary();
                                                return (
                                                    <TableRow hover key={`prompt-${prompt.id}`}>
                                                        {/* leaf rows: keep expander column but empty to maintain alignment */}
                                                        <TableCell width={12} />
                                                        <TableCell sx={{ pl: INDENT_PROMPT }}>
                                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                                <DialogPromptDescription dialogPrompt={prompt} />
                                                            </Box>
                                                        </TableCell>
                                                        <SummaryCells summary={dialogPromptSummary} />
                                                    </TableRow>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </>);
};

export default CourseExerciseActivity;
